/**
 * Insurance onboarding routing and context
 *
 * Resolves billing type, explain/checkout paths, and checkout table copy from either a
 * selected insurer or the patient's stored `webAppOnboarding` insurance fields.
 *
 * References:
 * - `insurance/config.ts` — route segments and billing-type config
 * - `insurance/metaBuilders.ts` — meta and displayed billing builders
 */

import type { Insurance, SignedUpPatient } from '@physiofit-digital/physiofit_types';
import type { OnboardingCheckoutTableCopy } from '../components/content/OnboardingCheckoutTable';
import {
  billingTypeRoutes,
  BNPL_CHECKOUT_ROUTE_SEGMENT,
  BNPL_PAYMENT_ROUTE_SEGMENT,
  BNPL_REJECTED_ROUTE_SEGMENT,
  COURSE_NOT_CERTIFIED_ROUTE_SEGMENT,
  resolveBlockedExplainRouteSegment,
  type BlockedExplainVariant,
  type CheckoutTableConfig,
  type InsuranceBillingType,
  type InsuranceMeta,
  type ResolvedCheckoutTable,
} from '../insurance/config';
import { formatEuro } from './copyUtils';
import { displayInsuranceName } from './displayInsuranceName';
import {
  buildInsuranceMetaFromInsurance,
  buildInsuranceMetaFromPatient,
  readDisplayedBillingFromWebAppOnboarding,
  resolveBillingTypeFromInsurance,
} from './metaBuilders';
import { readBillingPatientBlockedReason } from './normalize';

export type { InsuranceBillingType, InsuranceMeta } from '../insurance/config';

const ONBOARDING_BASE = '/webAppOnboarding';

export type ResolveInsuranceOnboardingInput =
  | {
      source: 'insurance';
      insurance: Insurance;
      billingType?: InsuranceBillingType;
      /** Practice-fee decision for this selection; the caller resolves it once (PF-5963). */
      practiceChargesFee?: boolean;
    }
  | { source: 'patient'; patient: SignedUpPatient | undefined };

export type InsuranceOnboardingContext = {
  billingType: InsuranceBillingType | null;
  insuranceId: string | null;
  insuranceName: string | null;
  explainPath: string | null;
  forwardPath: string | null;
  meta: InsuranceMeta;
  supportsInvoiceCheckout: boolean;
  checkoutTable: ResolvedCheckoutTable | null;
};

/**
 * Reads billing type from the patient's stored `webAppOnboarding.insuranceInfo`.
 *
 * @param {Object | undefined} patient Signed-up patient document.
 * @return {string | null} Invoice, blocked, abtretung, or null when unset.
 */
export function resolveBillingTypeFromPatient(patient: SignedUpPatient | undefined): InsuranceBillingType | null {
  const info = patient?.webAppOnboarding?.insuranceInfo;
  if (!info?.name?.trim() && !info?.id) {
    return null;
  }
  const billingType = info.billingType;
  if (
    billingType === 'invoice' ||
    billingType === 'blocked' ||
    billingType === 'abtretung' ||
    billingType === 'without_abtretung_physiofit' ||
    billingType === 'without_abtretung_patient' ||
    billingType === 'bnpl'
  ) {
    return billingType;
  }
  return null;
}

/**
 * Full path to the explain screen for a billing type (and blocked variant when applicable).
 *
 * @param {string} billingType Target billing flow.
 * @param {Object | undefined} blockedVariant Optional blocked reason and practice-fee decision for route segment.
 * @return {string} Absolute onboarding path.
 */
export function getInsuranceExplainPath(
  billingType: InsuranceBillingType,
  blockedVariant?: BlockedExplainVariant,
): string {
  const segment =
    billingType === 'blocked'
      ? resolveBlockedExplainRouteSegment(blockedVariant ?? {})
      : billingTypeRoutes[billingType].explainRouteSegment;
  return `${ONBOARDING_BASE}/${segment}`;
}

/**
 * Full path to the next step after the explain screen for a billing type.
 *
 * @param {string} billingType Target billing flow.
 * @return {string} Absolute onboarding path (checkout or install).
 */
export function getInsuranceForwardPath(billingType: InsuranceBillingType): string {
  const segment = billingTypeRoutes[billingType].forwardRouteSegment;
  return `${ONBOARDING_BASE}/${segment}`;
}

/** Full path to the not-certified process overview screen. */
export function getCourseNotCertifiedPath(): string {
  return `${ONBOARDING_BASE}/${COURSE_NOT_CERTIFIED_ROUTE_SEGMENT}`;
}

/** BNPL checkout (screen 08) — the financing screen's forward path. */
export function getBnplCheckoutPath(): string {
  return `${ONBOARDING_BASE}/${BNPL_CHECKOUT_ROUTE_SEGMENT}`;
}

/** BNPL payment method, consents and submit (screen 09) — the checkout screen's forward path. */
export function getBnplPaymentPath(): string {
  return `${ONBOARDING_BASE}/${BNPL_PAYMENT_ROUTE_SEGMENT}`;
}

/** BNPL provider decline screen — unused since ERS-81 made the decline silent, kept with the screen. */
export function getBnplRejectedPath(): string {
  return `${ONBOARDING_BASE}/${BNPL_REJECTED_ROUTE_SEGMENT}`;
}

export function buildCheckoutTableCopy(config: CheckoutTableConfig): OnboardingCheckoutTableCopy {
  return {
    courseFeeLabel: config.courseFeeLabel,
    courseFeeValue: formatEuro(config.courseFeeEur, 'plain'),
    insurerRefundLabel: config.reimbursementLabel,
    insurerRefundValue: formatEuro(config.reimbursementEur, 'deduct'),
    physiofitSubsidyLabel: config.subsidyLabel,
    physiofitSubsidyValue: config.subsidyEur > 0 ? formatEuro(config.subsidyEur, 'deduct') : '',
    yourCostsLabel: config.yourCostsLabel,
    yourCostsValue: formatEuro(config.yourCostsEur, 'plain'),
    insurerBonusLabel: config.bonusLabel,
    insurerBonusValue: config.bonusEur > 0 ? formatEuro(config.bonusEur, 'bonus') : '',
    footerLabel: config.footerLabel,
    footerValue: config.bonusEur > 0 ? formatEuro(config.bonusEur, 'plain') : '',
    a11ySummary: config.a11ySummary,
  };
}

function resolveCheckoutTable(meta: InsuranceMeta): ResolvedCheckoutTable | null {
  const tableConfig = meta.checkout?.table;
  if (!tableConfig) {
    return null;
  }
  return {
    ...buildCheckoutTableCopy(tableConfig),
    showPhysiofitSubsidy: tableConfig.showPhysiofitSubsidy,
    showFooter: tableConfig.showFooter,
  };
}

function resolveMetaFromPatient(patient: SignedUpPatient | undefined): InsuranceMeta {
  const web = patient?.webAppOnboarding;
  const info = web?.insuranceInfo;
  if (!info) {
    return {};
  }

  return buildInsuranceMetaFromPatient({
    billingType: info.billingType,
    insuranceName: info.name,
    blockedReason: info.blockedReason,
    displayedBilling: readDisplayedBillingFromWebAppOnboarding(web),
  });
}

function supportsInvoiceCheckoutForContext(billingType: InsuranceBillingType | null, meta: InsuranceMeta): boolean {
  return billingType === 'invoice' && meta.checkout?.table != null && meta.invoiceCheckout != null;
}

/**
 * Resolves billing type, routes, meta, and checkout table for insurer or patient input.
 *
 * @param {Object} input Either `{ source: 'insurance', insurance }` or `{ source: 'patient', patient }`.
 * @return {Object} Full onboarding insurance context for navigation and UI.
 */
export function resolveInsuranceOnboardingContext(input: ResolveInsuranceOnboardingInput): InsuranceOnboardingContext {
  if (input.source === 'insurance') {
    const { insurance } = input;
    const billingType = input.billingType ?? resolveBillingTypeFromInsurance(insurance);
    const insuranceName = displayInsuranceName(insurance);
    const meta = buildInsuranceMetaFromInsurance(insurance, insuranceName);

    const blockedReason = billingType === 'blocked' ? readBillingPatientBlockedReason(insurance) : undefined;

    return {
      billingType,
      insuranceId: insurance.id,
      insuranceName,
      explainPath: getInsuranceExplainPath(billingType, {
        blockedReason,
        practiceChargesFee: input.practiceChargesFee,
      }),
      forwardPath: getInsuranceForwardPath(billingType),
      meta,
      supportsInvoiceCheckout: supportsInvoiceCheckoutForContext(billingType, meta),
      checkoutTable: resolveCheckoutTable(meta),
    };
  }

  const patient = input.patient;
  const billingType = resolveBillingTypeFromPatient(patient);
  const insuranceId = patient?.webAppOnboarding?.insuranceInfo?.id?.trim() ?? null;
  const insuranceName = patient?.webAppOnboarding?.insuranceInfo?.name?.trim() ?? null;

  if (!billingType) {
    return {
      billingType: null,
      insuranceId,
      insuranceName,
      explainPath: null,
      forwardPath: null,
      meta: {},
      supportsInvoiceCheckout: false,
      checkoutTable: null,
    };
  }

  const meta = resolveMetaFromPatient(patient);
  const blockedReason = patient?.webAppOnboarding?.insuranceInfo?.blockedReason;
  const practiceChargesFee = patient?.webAppOnboarding?.insuranceInfo?.practiceChargesFee === true;

  return {
    billingType,
    insuranceId,
    insuranceName,
    explainPath: getInsuranceExplainPath(billingType, { blockedReason, practiceChargesFee }),
    forwardPath: getInsuranceForwardPath(billingType),
    meta,
    supportsInvoiceCheckout: supportsInvoiceCheckoutForContext(billingType, meta),
    checkoutTable: resolveCheckoutTable(meta),
  };
}
